import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import * as CustomTestPageActions from '../CustomTestsPage/actions';
import { userRoles } from 'constants/users';
import { useActions } from 'hooks/useActions';
import Path from 'routes/Path';
import QuestionAndOptions from './QuestionAndOptions';
import { Button, LinkButton } from 'components/Button/RoundButton';
import { Container, Wrapper } from 'components/Layout/Layout';
import TestFields from './TestFields';
import YesNoModal from 'components/Modals/yesNoModal';
import { Header, HeadingContainer, ButtonContainer, BannerContainer } from './styles';
import { useMultichoiceCreatorContext } from 'hooks/useContext';
import services from 'api/services';

const { TestService } = services;

export default function MultichoiceCreatorPage() {
  const navigate = useNavigate();
  const { testId } = useParams();

  const [confirmModalInfo, setConfirmModalInfo] = useState(undefined);
  const [savingTest, setSavingTest] = useState(false);

  const { userRole, user } = useSelector((state) => state.auth);
  const { copiedTestId } = useSelector((state) => state.CustomTestsPageReducer);

  const { copyTest, resetNewAndCopiedTest } = useActions(CustomTestPageActions);

  const { test, setTest } = useMultichoiceCreatorContext();

  useEffect(() => {
    if (!copiedTestId) return;
    let newTestId = copiedTestId;
    resetNewAndCopiedTest();
    setConfirmModalInfo(undefined);
    navigate(`/multichoice-creator/${newTestId}/`);
  }, [copiedTestId, navigate, resetNewAndCopiedTest]);

  const isPublishDisplayed = () => {
    if ((test.custom || test.archived) && userRole === userRoles.SUPER_ADMIN) {
      return true;
    } else if (
      (test.custom || test.archived) &&
      userRole === userRoles.COMPANY_ADMIN &&
      test.company === user.company
    ) {
      return true;
    } else {
      return false;
    }
  };

  const isUnpublishDisplayed = () => {
    if (!test.custom && !test.archived) {
      if (userRole === userRoles.SUPER_ADMIN) {
        return true;
      } else if (userRole === userRoles.COMPANY_ADMIN && test.company === user.company) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  };

  const isArchiveDisplayed = () => {
    if (test.custom || !test.archived) {
      if (userRole === userRoles.SUPER_ADMIN) {
        return true;
      } else if (userRole === userRoles.COMPANY_ADMIN && test.company === user.company) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  };

  const saveTest = async (body) => {
    try {
      if (
        body?.test?.type === 'test' &&
        body.test.hasOwnProperty('price') &&
        (body.test.price === '0' || body.test.price % 100 !== 0)
      ) {
        return toast.error('Price must be greater than zero and in multiple of 100');
      }
      if (savingTest) return toast.error('Already saving');
      setSavingTest(true);
      const { test } = await TestService.updateMCQTest(testId, body);
      setTest(test);
      toast.success('Test updated successfully');
    } catch (error) {
      toast.error(error?.message ?? error ?? 'Error saving test');
    } finally {
      setSavingTest(false);
    }
  };

  const onModalConfirm = async () => {
    if (confirmModalInfo.copyTest) return copyTest(testId, true);
    let params = {};
    if (confirmModalInfo.publishTest)
      params = {
        custom: false,
        archived: false,
      };
    else if (confirmModalInfo.unpublishTest)
      params = {
        custom: true,
        archived: false,
      };
    else if (confirmModalInfo.archiveTest)
      params = {
        custom: false,
        archived: true,
      };
    await saveTest({ test: params });
    setConfirmModalInfo(undefined);
  };

  const creatorHeaderButtons = () => (
    <ButtonContainer>
      <LinkButton removeMargin fontSize="14px" primaryBlue primaryGreenBorder href={Path.customTests}>
        <i className="fa fa-arrow-left mr-1" />
        All Tests
      </LinkButton>
      <Button
        disabled={savingTest}
        removeMargin
        fontSize="14px"
        onClick={() => {
          setConfirmModalInfo({
            title: 'Confirmation',
            body: 'Do you want to copy the test?',
            copyTest: true,
          });
        }}
        primaryBlue
        primaryGreenBorder
      >
        <i className="fa fa-copy mr-1" />
        Copy
      </Button>
      {isPublishDisplayed() && (
        <Button
          disabled={savingTest}
          removeMargin
          fontSize="14px"
          onClick={() => {
            setConfirmModalInfo({
              title: 'Confirmation',
              body: 'Do you want to publish the test?',
              publishTest: true,
            });
          }}
          primaryBlue
          primaryGreenBorder
        >
          <i className="fa fa-bullhorn mr-1" />
          Publish
        </Button>
      )}
      {isUnpublishDisplayed() && (
        <Button
          disabled={savingTest}
          removeMargin
          fontSize="14px"
          onClick={() => {
            setConfirmModalInfo({
              title: 'Confirmation',
              body: 'Do you want to unpublish the test?',
              unpublishTest: true,
            });
          }}
          primaryBlue
          primaryGreenBorder
        >
          <i className="fa fa-bullhorn mr-1" />
          Unpublish
        </Button>
      )}
      {isArchiveDisplayed() && (
        <Button
          disabled={savingTest}
          primaryRed
          removeMargin
          fontSize="14px"
          margin
          onClick={() => {
            setConfirmModalInfo({
              title: 'Confirmation',
              body: 'Do you want to archive the test?',
              archiveTest: true,
            });
          }}
          primaryBlue
          primaryGreenBorder
        >
          <i className="fa fa-bullhorn mr-1" />
          Archive
        </Button>
      )}
      <Button
        disabled={savingTest}
        removeMargin
        fontSize="14px"
        onClick={() => saveTest({ test })}
        primaryBlue
        primaryGreenBorder
      >
        <i className="fa fa-save mr-1" />
        Save Test
      </Button>
    </ButtonContainer>
  );

  return (
    <Wrapper padding="50px 0 0 0" minHeight="100vh">
      {confirmModalInfo && (
        <YesNoModal
          modalInfo={confirmModalInfo}
          confirm={(isConfirmed) => {
            if (isConfirmed) return onModalConfirm();
            setConfirmModalInfo(undefined);
          }}
        />
      )}
      <Container column>
        {test && (
          <>
            {test?.disabledEditing && <BannerContainer>Online editing of this test has been disabled</BannerContainer>}
            <HeadingContainer>
              <Header>Edit Test</Header>
              {creatorHeaderButtons()}
            </HeadingContainer>
            <TestFields />
            <QuestionAndOptions />
          </>
        )}
      </Container>
    </Wrapper>
  );
}
